import * as React from 'react'
import { Inbox } from 'lucide-react'
import { useOwnQuoteRequests } from '@/hooks/useQuoteRequests'
import { useOwnVendor } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { formatEventDate } from '@/lib/eventTime'
import { serverPage } from '@/lib/pagination'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states'
import { Pagination } from '@/components/ui/pagination'
import { Table, TableActionsCell, TableActionsHead, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { requesterLabel } from '@/components/quotes/EventQuoteRequests'
import type { QuoteRequestDto } from '@lib/dto/quote-request.dto'

const RECEIVED = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
const PREVIEW = 80

/** The event, as the request was sent: "Jun 5, 2027 · Driftwood · 150 guests". */
function eventLine(request: QuoteRequestDto): string {
  const { date, venueName, guestCount } = request.event
  return [date === null ? 'Date not set' : formatEventDate(date), venueName, guestCount === null ? 'Guests not set' : `${String(guestCount)} guests`].join(' · ')
}

/** The message, its start until opened when it runs long. */
function Message({ text }: { text: string }) {
  if (text.length <= PREVIEW) return <span className="whitespace-pre-line">{text}</span>
  return (
    <details>
      <summary className="cursor-pointer">{text.slice(0, PREVIEW).trimEnd()}…</summary>
      <p className="m-0 mt-1 whitespace-pre-line">{text}</p>
    </details>
  )
}

function RequestRow({ request }: { request: QuoteRequestDto }) {
  return (
    <TableRow>
      <TableCell className="whitespace-nowrap text-sm text-ink-muted">{RECEIVED.format(new Date(request.sentAt))}</TableCell>
      <TableCell>
        <span className="block font-medium">{request.requester.name}</span>
        <span className="block text-xs text-ink-muted">{requesterLabel(request.requester.role, request.requester.company)}</span>
      </TableCell>
      <TableCell className="text-sm">{eventLine(request)}</TableCell>
      <TableCell className="text-sm">{request.packages.length === 0 ? 'None chosen' : request.packages.join(', ')}</TableCell>
      <TableCell className="max-w-xs text-sm">
        <Message text={request.message} />
      </TableCell>
      <TableActionsCell>
        <a
          href={`mailto:${request.requester.email}?subject=${encodeURIComponent('Your quote request')}`}
          className="text-sm underline underline-offset-2 hover:text-ink"
        >
          Reply
        </a>
      </TableActionsCell>
    </TableRow>
  )
}

/**
 * The quote requests a vendor company has been sent (docs/vendor-growth.spec.md,
 * "Quote requests"), newest first, for everyone at the company: each also went
 * by email to the person it chose, or its owners. A reply goes to whoever asked.
 */
export function VendorRequestsPage() {
  const [page, setPage] = React.useState(1)
  const requests = useOwnQuoteRequests(page)
  const company = useOwnVendor()

  function body() {
    if (requests.isPending) return <LoadingState label="Loading quote requests…" />
    if (requests.isError) return <ErrorState message={requestErrorMessage(requests.error)} onRetry={() => void requests.refetch()} />
    const list = requests.data
    if (list.total === 0) {
      return (
        <EmptyState
          icon={Inbox}
          title="No quote requests yet"
          description={`When couples, planners or venue staff ask ${company.data?.name ?? 'your company'} for a quote, they arrive here and by email.`}
        />
      )
    }
    const shown = serverPage(page, list.total, list.requests.length)
    return (
      <Table
        heading={`${String(list.total)} ${list.total === 1 ? 'request' : 'requests'}`}
        footer={
          <Pagination page={shown.page} pageCount={shown.pageCount} from={shown.from} to={shown.to} total={shown.total} unit="requests" onPage={setPage} />
        }
      >
        <TableHeader>
          <TableHead>Received</TableHead>
          <TableHead>From</TableHead>
          <TableHead>Event</TableHead>
          <TableHead>Packages</TableHead>
          <TableHead>Message</TableHead>
          <TableActionsHead />
        </TableHeader>
        <TableBody>
          {list.requests.map((request) => (
            <RequestRow key={request.id} request={request} />
          ))}
        </TableBody>
      </Table>
    )
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <PageHeader title="Requests" description="Quote requests from couples, planners and venue staff. Reply by email." />
      {body()}
    </PageContainer>
  )
}
